import { useTranslation } from "react-i18next";
import { Calendar, TrendingUp } from "lucide-react";
import { StatCard, WalletHeroCard, WalletHeroChip } from "@/components/common";
import { formatMoney } from "@/lib/money";

interface DashboardStatsProps {
  walletBalance: number;
  currentBookings: number;
  bookingChange: number;
  currentRevenue: number;
  revenueChange: number;
  transactionsCount: number;
}

/**
 * Dashboard stats grid showing key metrics.
 */
export function DashboardStats({
  walletBalance,
  currentBookings,
  bookingChange,
  currentRevenue,
  revenueChange,
  transactionsCount,
}: DashboardStatsProps) {
  const { t } = useTranslation("dashboard");
  const trendVsPrev = t("partner.statsTrendVsPrev");
  const isAmountOwed = walletBalance < 0;

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
      <WalletHeroCard
        className="col-span-2"
        label={t(
          isAmountOwed
            ? "partner.statsAmountOwed"
            : "partner.statsWalletBalance",
        )}
        amount={formatMoney(Math.abs(walletBalance))}
        isAmountOwed={isAmountOwed}
        link={{ to: "/wallet", label: t("shared.wallet") }}
        meta={
          <WalletHeroChip>
            {t("partner.statsTransactions30d")}: {transactionsCount}
          </WalletHeroChip>
        }
      />

      <StatCard
        title={t("partner.statsBookings30d")}
        value={currentBookings}
        icon={Calendar}
        iconColor="primary"
        trend={
          bookingChange !== 0
            ? {
                value: Math.abs(bookingChange),
                isPositive: bookingChange > 0,
                label: trendVsPrev,
              }
            : undefined
        }
      />

      <StatCard
        title={t("partner.statsRevenue30d")}
        value={formatMoney(currentRevenue)}
        icon={TrendingUp}
        iconColor="accent"
        trend={
          revenueChange !== 0
            ? {
                value: Math.abs(revenueChange),
                isPositive: revenueChange > 0,
                label: trendVsPrev,
              }
            : undefined
        }
      />
    </div>
  );
}
